import React, { useState } from 'react';
import { adminAuthHeaders } from './adminCheckoutUtils';

function formatMoney(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return '₹0';
  return `₹${x.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}

const STATUS_LABELS = {
  requested: 'Requested',
  approved: 'Approved',
  rejected: 'Rejected',
  received: 'Received',
  refunded: 'Refunded',
  closed: 'Closed',
};

function statusBadgeClass(status) {
  return `admin-order-returns__badge admin-order-returns__badge--${status || 'default'}`;
}

export default function AdminOrderReturnsSection({
  sessionToken,
  orderNumber,
  detail,
  onOrderRefresh,
  setStatus,
}) {
  const [busyKey, setBusyKey] = useState('');
  const returns = Array.isArray(detail?.returns) ? detail.returns : [];
  const policy = detail?.returns_policy;
  const rzPayment = detail?.razorpay_payment_id;
  const paymentMethod = String(detail?.payment_method || '').toLowerCase();
  const canRazorpayRefund = Boolean(rzPayment) && (paymentMethod === 'online' || paymentMethod === '');

  if (!policy?.enabled && returns.length === 0) {
    return null;
  }

  const postReturnAction = async (action, body, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) {
      return;
    }
    const key = `${action}-${body.return_number || ''}-${
      body.return_status ?? (action === 'return_process_refund' ? 'rz' : '')
    }`;
    setBusyKey(key);
    setStatus('');
    try {
      const r = await fetch('/api/admin_orders.php', {
        method: 'POST',
        headers: adminAuthHeaders(sessionToken),
        body: JSON.stringify({
          session_token: sessionToken,
          action,
          order_number: orderNumber,
          ...body,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.success) {
        setStatus(d.error || 'Return action failed.');
        return;
      }
      if (d.order && onOrderRefresh) {
        onOrderRefresh(d.order);
      }
      setStatus(
        action === 'return_process_refund' ? 'Refund processed.' : 'Return updated.'
      );
    } catch (e) {
      setStatus(e.message || 'Network error');
    } finally {
      setBusyKey('');
    }
  };

  const isBusy = (action, ret, bodyExtra = '') =>
    busyKey === `${action}-${ret.return_number}-${bodyExtra}`;

  return (
    <section className="admin-order-returns">
      <div className="admin-order-returns__head">
        <h4 className="admin-orders-section-title mb-0">Returns</h4>
        {policy?.enabled ? (
          <span className="admin-order-returns__policy small text-muted">
            {policy.window_days > 0
              ? `${policy.window_days}-day window`
              : 'No day limit'}{' '}
            · default{' '}
            {policy.default_allowance === 'non_returnable' ? 'non-returnable' : 'returnable'}
          </span>
        ) : (
          <span className="small text-muted">Returns disabled in checkout settings</span>
        )}
      </div>

      {returns.length === 0 ? (
        <p className="admin-order-returns__empty small text-muted mb-0">
          No return requests for this order.
        </p>
      ) : (
        <ul className="admin-order-returns__list list-unstyled mb-0">
          {returns.map((ret) => {
            const st = String(ret.status || '').toLowerCase();
            const refundSt = String(ret.refund_status || 'none').toLowerCase();
            return (
              <li key={ret.return_number} className="admin-order-returns__card">
                <div className="admin-order-returns__card-head">
                  <div>
                    <code className="admin-order-returns__number">{ret.return_number}</code>
                    <span className={statusBadgeClass(st)}>{STATUS_LABELS[st] || st}</span>
                    {refundSt !== 'none' ? (
                      <span
                        className={`admin-order-returns__badge admin-order-returns__badge--refund-${refundSt}`}
                      >
                        Refund {refundSt}
                      </span>
                    ) : null}
                  </div>
                  <time className="small text-muted">
                    {ret.created_at ? new Date(ret.created_at).toLocaleString('en-IN') : ''}
                  </time>
                </div>

                <p className="admin-order-returns__reason small mb-2">
                  <span className="text-muted">Customer reason:</span> {ret.reason}
                </p>

                <table className="table table-sm admin-order-returns__items mb-2">
                  <tbody>
                    {(ret.items || []).map((it) => (
                      <tr key={`${ret.return_number}-${it.order_item_id}`}>
                        <td>{it.product_name}</td>
                        <td className="text-end">× {it.quantity}</td>
                        <td className="text-end">{formatMoney(it.line_refund)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <th colSpan={2} className="small">
                        Refund total
                      </th>
                      <th className="text-end">{formatMoney(ret.refund_amount)}</th>
                    </tr>
                  </tfoot>
                </table>

                {ret.razorpay_refund_id ? (
                  <p className="small mb-2">
                    Razorpay refund: <code>{ret.razorpay_refund_id}</code>
                  </p>
                ) : null}
                {ret.refund_error ? (
                  <p className="small text-danger mb-2" role="alert">
                    {ret.refund_error}
                    {refundSt === 'failed' ? ' Use the refund button below to retry.' : ''}
                  </p>
                ) : null}

                <div className="admin-order-returns__actions">
                  {st === 'requested' ? (
                    <>
                      <button
                        type="button"
                        className="btn btn-sm btn-success"
                        disabled={Boolean(busyKey)}
                        onClick={() =>
                          postReturnAction('return_update_status', {
                            return_number: ret.return_number,
                            return_status: 'approved',
                          })
                        }
                      >
                        {isBusy('return_update_status', ret, 'approved') ? 'Saving…' : 'Approve'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        disabled={Boolean(busyKey)}
                        onClick={() =>
                          postReturnAction(
                            'return_update_status',
                            {
                              return_number: ret.return_number,
                              return_status: 'rejected',
                            },
                            'Reject this return request?'
                          )
                        }
                      >
                        Reject
                      </button>
                    </>
                  ) : null}
                  {st === 'approved' ? (
                    <>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        disabled={Boolean(busyKey)}
                        onClick={() =>
                          postReturnAction('return_update_status', {
                            return_number: ret.return_number,
                            return_status: 'received',
                          })
                        }
                      >
                        {isBusy('return_update_status', ret, 'received') ? 'Saving…' : 'Mark received'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        disabled={Boolean(busyKey)}
                        onClick={() =>
                          postReturnAction(
                            'return_update_status',
                            {
                              return_number: ret.return_number,
                              return_status: 'rejected',
                            },
                            'Reject this return? The customer can submit a new request if items are still eligible.'
                          )
                        }
                      >
                        Reject
                      </button>
                    </>
                  ) : null}
                  {st === 'received' && refundSt !== 'completed' ? (
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      disabled={Boolean(busyKey)}
                      onClick={() =>
                        postReturnAction(
                          'return_process_refund',
                          {
                            return_number: ret.return_number,
                            skip_razorpay: !canRazorpayRefund,
                          },
                          canRazorpayRefund
                            ? `Issue Razorpay refund of ${formatMoney(ret.refund_amount)}?`
                            : 'Mark this return as refunded (offline payment)?'
                        )
                      }
                    >
                      {isBusy('return_process_refund', ret, 'rz')
                        ? 'Processing…'
                        : canRazorpayRefund
                          ? 'Refund via Razorpay'
                          : 'Mark refunded (offline)'}
                    </button>
                  ) : null}
                </div>

                {st === 'requested' ? (
                  <p className="admin-order-returns__hint small text-muted mb-0 mt-2">
                    Approve → customer ships items → Mark received → Refund.
                  </p>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
